// Revue d'une lecture assistée (lot L7) : état de la machine dépôt/analyse, actualisation, relance après un
// échec, puis revue de chaque proposition (confirmer, corriger, rejeter) avec ses pages sources. Une
// proposition relue reste une information : elle ne modifie aucun dossier ni aucune décision.
import {
  ETATS_LECTURE,
  FINALITES_LECTURE,
  REVUES_PROPOSITION,
  type PropositionLecture,
} from '@cc/contrats';
import { Bandeau, Bouton, Champ, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { actualiser, annuler, INVALIDER_ASSISTANCE, relancer, revoir, useLecture } from './api';
import { TON_LECTURE } from './PageAssistance';
import styles from '../achats/achats.module.css';

const ETATS_SOURCE = { present: 'Lu', absent: 'Absent', ambiguous: 'Ambigu' } as const;

export function PageLecture() {
  const { id } = useParams();
  const { contexte } = useSession();
  const lecture = useLecture(id);
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const modifier = peut(contexte, 'document', 'modifier');
  const opts = { invalider: INVALIDER_ASSISTANCE };
  const maj = useAction({
    executer: () => actualiser(id as string),
    succes: (l) => `État : ${ETATS_LECTURE[l.etat]}.`,
    ...opts,
  });
  const relance = useAction({
    executer: () => relancer(id as string),
    succes: (l) => `État : ${ETATS_LECTURE[l.etat]}.`,
    ...opts,
  });
  const annulation = useAction({
    executer: () => annuler(id as string),
    succes: 'Lecture annulée.',
    ...opts,
  });
  const revue = useAction({
    executer: (e: { p: string; decision: 'confirmee' | 'corrigee' | 'rejetee'; valeur?: string }) =>
      revoir(id as string, e.p, { decision: e.decision, valeur: e.valeur }),
    succes: 'Proposition relue.',
    ...opts,
  });
  const l = lecture.data;
  const erreur =
    maj.messageErreur ?? relance.messageErreur ?? annulation.messageErreur ?? revue.messageErreur;
  const colonnes: ColonneTableau<PropositionLecture>[] = [
    {
      id: 'champ',
      entete: 'Champ',
      enTeteLigne: true,
      masquable: false,
      cellule: (p) => <span className="cc-mono">{p.champ}</span>,
    },
    { id: 'source', entete: 'Lecture', cellule: (p) => ETATS_SOURCE[p.etat_source] },
    {
      id: 'valeur',
      entete: 'Valeur proposée',
      cellule: (p) =>
        p.valeur_proposee ??
        (p.candidats.length > 0 ? `Candidats : ${p.candidats.join(', ')}` : 'aucune'),
    },
    {
      id: 'preuves',
      entete: 'Source',
      cellule: (p) =>
        p.preuves.length === 0
          ? 'aucune'
          : p.preuves
              .map((x) => (x.page ? `page ${x.page}` : (x.methode ?? 'non précisée')))
              .join(', '),
    },
    {
      id: 'revue',
      entete: 'Revue',
      cellule: (p) =>
        p.revue !== 'a_relire' ? (
          `${REVUES_PROPOSITION[p.revue]}${p.valeur_retenue ? ` : ${p.valeur_retenue}` : ''}${p.relue_par ? ` (${p.relue_par})` : ''}`
        ) : l?.etat === 'a_relire' && modifier ? (
          <div className={styles.actesLigne}>
            {p.valeur_proposee !== null ? (
              <Bouton
                variante="secondaire"
                onClick={() => revue.lancer({ p: p.id, decision: 'confirmee' })}
              >
                Confirmer
              </Bouton>
            ) : null}
            <Champ
              libelle={`Valeur lue pour ${p.champ}`}
              value={corrections[p.id] ?? ''}
              onChange={(e) => setCorrections((c) => ({ ...c, [p.id]: e.target.value }))}
              facultatif
            />
            <Bouton
              variante="secondaire"
              disabled={!corrections[p.id]}
              onClick={() =>
                revue.lancer({ p: p.id, decision: 'corrigee', valeur: corrections[p.id] })
              }
            >
              Corriger
            </Bouton>
            <Bouton
              variante="secondaire"
              onClick={() => revue.lancer({ p: p.id, decision: 'rejetee' })}
            >
              Rejeter
            </Bouton>
          </div>
        ) : (
          REVUES_PROPOSITION[p.revue]
        ),
    },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Lecture assistée"
        titre={l ? `Lecture de « ${l.document.titre} »` : 'Lecture assistée'}
        description={
          l
            ? `${FINALITES_LECTURE[l.finalite]} ; ${l.mode === 'reel' ? `raccordement réel vers ${l.destination ?? ''}` : 'simulateur local, aucune transmission hors du serveur'} ; demandée par ${l.demandee_par}.`
            : undefined
        }
        actions={<Lien href="/assistance-documentaire">Toutes les lectures</Lien>}
      />
      <EtatRequete
        chargement={lecture.isPending}
        erreur={lecture.error}
        onReessayer={() => void lecture.refetch()}
        lot="L7"
        fonction="La lecture"
      >
        {l ? (
          <>
            <div className={styles.actes}>
              <PuceEtat ton={TON_LECTURE[l.etat]} libelle={ETATS_LECTURE[l.etat]} />
              {['deposee', 'en_analyse'].includes(l.etat) ? (
                <Bouton
                  variante="principal"
                  chargement={maj.enCours}
                  onClick={() => maj.lancer(undefined)}
                >
                  Actualiser
                </Bouton>
              ) : null}
              {l.etat === 'echec' && modifier ? (
                <Bouton
                  variante="secondaire"
                  chargement={relance.enCours}
                  onClick={() => relance.lancer(undefined)}
                >
                  Relancer
                </Bouton>
              ) : null}
              {!['relue', 'annulee'].includes(l.etat) && modifier ? (
                <Bouton variante="secondaire" onClick={() => annulation.lancer(undefined)}>
                  Annuler la lecture
                </Bouton>
              ) : null}
            </div>
            {l.etat === 'echec' ? (
              <Bandeau type="attention">{`Échec (${l.erreur ?? 'erreur'}), après ${l.tentatives} tentative(s) : l'instruction se poursuit manuellement ; une relance réutilise la même clé et ne crée pas de doublon.`}</Bandeau>
            ) : null}
            {l.etat === 'relue' ? (
              <Bandeau type="info">
                Toutes les propositions sont relues. Elles ne modifient aucun dossier : reportez
                vous-même les valeurs retenues.
              </Bandeau>
            ) : null}
            {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
            <section className={styles.bloc} aria-labelledby="titre-propositions">
              <h2 id="titre-propositions">Propositions</h2>
              {l.propositions.length === 0 ? (
                <p className={styles.detail}>Aucune proposition reçue pour le moment.</p>
              ) : (
                <Tableau
                  legende="Propositions de la lecture"
                  colonnes={colonnes}
                  lignes={l.propositions}
                  cleLigne={(p) => p.id}
                  cleColonnes="assistance-propositions"
                />
              )}
            </section>
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
